import React from 'react';
import { Brain, Code, DollarSign, Hash, Activity } from 'lucide-react';
import { ProjectTokenStats } from '../types/chat';
import { formatCost } from '../utils/formatCost';
import { canShowCost } from '../utils/usageConfig';
import { useAuth } from '../contexts/AuthContext';

interface ProjectTokenStatsDisplayProps {
  tokenStats: ProjectTokenStats;
  className?: string;
}

const ProjectTokenStatsDisplay: React.FC<ProjectTokenStatsDisplayProps> = ({
  tokenStats,
  className = ""
}) => {
  const { hasAdminRole } = useAuth();
  const showCost = canShowCost(hasAdminRole);

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  return (
    <div className={`bg-white rounded-lg ${className}`}>
      {/* Total Summary */}
      <div className="mb-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Token Usage Analytics</h4>
        <div className={`grid ${showCost ? 'grid-cols-2' : 'grid-cols-1'} gap-6 mb-4`}>
          {showCost && (
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">Total Cost</span>
              </div>
              <div className="text-2xl font-bold text-green-700">
                {formatCost(tokenStats.total_cost)}
              </div>
            </div>
          )}
          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <Activity className="h-5 w-5 text-blue-600" />
              <span className="font-medium text-blue-800">Total Requests</span>
            </div>
            <div className="text-2xl font-bold text-blue-700">
              {tokenStats.total_requests}
            </div>
          </div>
        </div>
        {/* Total Token Breakdown */}
        {(tokenStats.input_tokens !== undefined || tokenStats.output_tokens !== undefined) && (
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="text-sm text-purple-600 mb-1">Total Tokens</div>
              <div className="text-lg font-bold text-purple-800">
                {formatNumber(tokenStats.total_tokens)}
              </div>
            </div>
            <div className="text-center p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="text-sm text-indigo-600 mb-1">Total Input Tokens</div>
              <div className="text-lg font-bold text-indigo-800">
                {formatNumber(tokenStats.input_tokens || 0)}
              </div>
            </div>
            <div className="text-center p-3 bg-pink-50 rounded-lg border border-pink-200">
              <div className="text-sm text-pink-600 mb-1">Total Output Tokens</div>
              <div className="text-lg font-bold text-pink-800">
                {formatNumber(tokenStats.output_tokens || 0)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mode Breakdown */}
      <div className="space-y-4">
        {/* Brainstorm Mode */}
        <div className="border border-orange-200 rounded-lg p-4 bg-orange-50">
          <div className="flex items-center space-x-2 mb-3">
            <Brain className="h-5 w-5 text-orange-600" />
            <h5 className="font-semibold text-orange-800">Prototype Mode</h5>
          </div>
          <div className={`grid ${showCost ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mb-3`}>
            {showCost && (
              <div className="text-center">
                <div className="text-sm text-orange-600 mb-1">Cost</div>
                <div className="font-bold text-orange-800">{formatCost(tokenStats.brainstorm_cost)}</div>
              </div>
            )}
            <div className="text-center">
              <div className="text-sm text-orange-600 mb-1">Requests</div>
              <div className="font-bold text-orange-800">{tokenStats.brainstorm_requests}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-orange-600 mb-1">Tokens</div>
              <div className="font-bold text-orange-800 mb-2">
                {formatNumber(tokenStats.brainstorm_tokens)}
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800">
                  Input: {formatNumber(tokenStats.brainstorm_input_tokens || 0)}
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-pink-100 text-pink-800">
                  Output: {formatNumber(tokenStats.brainstorm_output_tokens || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Development Mode */}
        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
          <div className="flex items-center space-x-2 mb-3">
            <Code className="h-5 w-5 text-blue-600" />
            <h5 className="font-semibold text-blue-800">Development Mode</h5>
          </div>
          <div className={`grid ${showCost ? 'grid-cols-3' : 'grid-cols-2'} gap-4 mb-3`}>
            {showCost && (
              <div className="text-center">
                <div className="text-sm text-blue-600 mb-1">Cost</div>
                <div className="font-bold text-blue-800">{formatCost(tokenStats.development_cost)}</div>
              </div>
            )}
            <div className="text-center">
              <div className="text-sm text-blue-600 mb-1">Requests</div>
              <div className="font-bold text-blue-800">{tokenStats.development_requests}</div>
            </div>
            <div className="text-center">
              <div className="text-sm text-blue-600 mb-1">Tokens</div>
              <div className="font-bold text-blue-800 mb-2">
                {formatNumber(tokenStats.development_tokens)}
              </div>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-indigo-100 text-indigo-800">
                  Input: {formatNumber(tokenStats.development_input_tokens || 0)}
                </span>
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-pink-100 text-pink-800">
                  Output: {formatNumber(tokenStats.development_output_tokens || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectTokenStatsDisplay;
